import ModalContainer from 'components/modal/modal-container'

import ModalActions from 'components/modal/modal-actions'
import { Button } from '@mui/material'

import { closeModal } from 'actions/app'

import { archiveClients } from 'api/client'
import { useTranslation } from 'react-i18next'

import type { AppState } from 'state/index'
import { useDispatch, useSelector } from 'react-redux'
import { get, find } from 'es-toolkit/compat'
import { useEffect, useState } from 'react'

interface ArchiveClientProps {
  selectedClients: {
    id: number
    areaSqm: string | 0
    name: string
    farmCount: number
    fieldCount: number
  }[]
  onSuccess?: () => void
}

export default function ArchiveClient(props: ArchiveClientProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { selectedClients, onSuccess } = props

  const clients = useSelector((state) =>
    get(state as AppState, 'client.collection', []),
  )

  const defaultClient = useSelector(
    (state) => (state as AppState).account.singleton.prefs?.cff?.client,
  )

  const [defaultDeletionMsg, showDefaultDeletionMsg] = useState(false)
  const [showActiveFarmsMsg, setShowActiveFarmsMsg] = useState(false)

  useEffect(() => {
    if (
      (defaultClient &&
        selectedClients.find(
          //@ts-expect-error
          (client) => parseInt(defaultClient) === parseInt(client.id),
        )) ||
      clients.length === 1
    ) {
      showDefaultDeletionMsg(true)
    }
  }, [selectedClients, defaultClient, clients])

  useEffect(() => {
    let _showActiveFarmsMsg = false
    for (let i = 0; i < selectedClients.length && !_showActiveFarmsMsg; i++) {
      _showActiveFarmsMsg = selectedClients[i].farmCount > 0
    }
    setShowActiveFarmsMsg(_showActiveFarmsMsg)
  }, [selectedClients])

  const closePopup = () => dispatch(closeModal())

  const archiveItem = () => {
    const clientIds = selectedClients.map((client) => client.id)
    // There can be active farms in the client that do not show up if they have no boundary. To handle this case, we
    // cascade the archive action to the farms when archiving a client, if the farm count is 0 to handle this case.
    // Otherwise the user will need to archive the farms separately before being able to archive the client.
    //@ts-expect-error
    dispatch(archiveClients({ clientIds, cascade: !showActiveFarmsMsg })).then(
      () => {
        if (onSuccess) {
          onSuccess()
        }
      },
    )
  }

  return (
    <ModalContainer>
      {defaultDeletionMsg ? (
        <>
          <div>
            {t('cannot_delete_default', {
              entity: t('client'),
            })}
          </div>
          <ModalActions>
            <Button variant="contained" color="primary" onClick={closePopup}>
              {t('ok')}
            </Button>
          </ModalActions>
        </>
      ) : showActiveFarmsMsg ? (
        <>
          {selectedClients.map((client) => {
            if (client.farmCount > 0) {
              return (
                <div key={client.id}>
                  {t('delete_child_entity_first', {
                    parentEntityName: client.name,
                    childEntity: t('farms'),
                    parentEntity: t('client'),
                  })}
                </div>
              )
            }
          })}

          <ModalActions>
            <Button variant="contained" color="primary" onClick={closePopup}>
              {t('ok')}
            </Button>
          </ModalActions>
        </>
      ) : (
        <>
          <div>
            {t('archive_item_warning', {
              item: selectedClients.length > 1 ? t('clients') : t('client'),
            })}
          </div>
          <br />
          <ModalActions>
            <Button variant="contained" color="secondary" onClick={closePopup}>
              {t('cancel')}
            </Button>
            <Button variant="contained" color="primary" onClick={archiveItem}>
              {t('archive')}
            </Button>
          </ModalActions>
        </>
      )}
    </ModalContainer>
  )
}
